import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClipboardList, MessageSquare, FileText, DollarSign, History } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Workforce, WorkforceComment, calculateMargin } from "@/types/workforce";
import { WorkforceComments } from "./WorkforceComments";
import { WorkforceHistoryTab } from "./WorkforceHistoryTab";
import { WorkforceDocumentsTab } from "./WorkforceDocumentsTab";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { DynamicTabBar, Tab } from "@/components/ui/DynamicTabBar";
import { useAuth } from "@/providers/AuthProvider";

interface WorkforceTabsProps {
	workforce: Workforce;
	comments: WorkforceComment[];
	activeTab: string;
	setActiveTab: (tab: string) => void;
	onAddComment: (content: string) => Promise<void>;
	onDeleteComment: (commentId: string) => Promise<void>;
	onUpdateComment: (commentId: string, content: string) => Promise<void>;
}

export const WorkforceTabs = ({
	workforce,
	comments,
	activeTab,
	setActiveTab,
	onAddComment,
	onDeleteComment,
	onUpdateComment,
}: WorkforceTabsProps) => {
	const { t } = useTranslation();
	const { user } = useAuth();

	const workforceTabConfig: Tab[] = useMemo(() => [
		{ id: "overview", label: t("workforce.tabs.overview", "Overview"), icon: <ClipboardList className="h-4 w-4" /> },
		{ id: "financial", label: t("workforce.tabs.financial", "Financial"), icon: <DollarSign className="h-4 w-4" /> },
		{ id: "comments", label: t("workforce.tabs.comments", "Comments"), icon: <MessageSquare className="h-4 w-4" /> },
		{ id: "documents", label: t("workforce.tabs.documents", "Documents"), icon: <FileText className="h-4 w-4" /> },
		{ id: "history", label: t("workforce.tabs.history", "History"), icon: <History className="h-4 w-4" /> },
	], [t]);

	// Calculate margins
	const dailyMargin = calculateMargin(workforce.purchase_price_daily, workforce.selling_price_daily);
	const hourlyMargin = calculateMargin(workforce.purchase_price_hourly, workforce.selling_price_hourly);

	return (
		<Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
			<DynamicTabBar
				tabs={workforceTabConfig}
				pinnedIds={["overview", "financial", "comments"]}
				dynamicSlotCount={2}
				defaultDynamicIds={["documents", "history"]}
				activeTabId={activeTab}
				onTabChange={setActiveTab}
				entityType="workforce"
				userId={user?.id || ""}
			/>

			{/* Overview Tab */}
			<TabsContent value="overview" className="mt-4">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* Assignment Details */}
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">
								{t("workforce.overview.summary", "Assignment Summary")}
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							{workforce.job ? (
								<div>
									<div className="text-sm text-muted-foreground mb-1">
										{t("workforce.overview.jobDetails", "Job Details")}
									</div>
									<p className="text-sm">{workforce.job.title}</p>
									{workforce.job.company && (
										<p className="text-sm text-muted-foreground">@ {workforce.job.company.name}</p>
									)}
								</div>
							) : workforce.external_company_name ? (
								<div>
									<div className="text-sm text-muted-foreground mb-1">
										{t("workforce.overview.externalAssignment", "External Assignment")}
									</div>
									<p className="text-sm font-medium">{workforce.external_job_title || '-'}</p>
									<p className="text-sm text-muted-foreground">@ {workforce.external_company_name}</p>
									{workforce.external_company_contact && (
										<p className="text-sm text-muted-foreground mt-1">
											{t("workforce.fields.clientContact", "Contact")}: {workforce.external_company_contact}
										</p>
									)}
								</div>
							) : null}

							{workforce.external_job_description && (
								<div>
									<div className="text-sm text-muted-foreground mb-1">
										{t("workforce.fields.description", "Description")}
									</div>
									<p className="text-sm whitespace-pre-wrap">{workforce.external_job_description}</p>
								</div>
							)}

							{workforce.notes && (
								<div>
									<div className="text-sm text-muted-foreground mb-1">
										{t("workforce.fields.notes", "Notes")}
									</div>
									<p className="text-sm whitespace-pre-wrap">{workforce.notes}</p>
								</div>
							)}
						</CardContent>
					</Card>

					{/* Consultant Info */}
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">
								{t("workforce.fields.consultant", "Consultant")}
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							{workforce.candidate && (
								<>
									<div>
										<div className="text-sm text-muted-foreground">
											{t("common.name", "Name")}
										</div>
										<p className="font-medium">{workforce.candidate.name}</p>
									</div>
									{workforce.candidate.function && (
										<div>
											<div className="text-sm text-muted-foreground">
												{t("workforce.fields.role", "Role")}
											</div>
											<p>{workforce.candidate.function}</p>
										</div>
									)}
									{workforce.candidate.email && (
										<div>
											<div className="text-sm text-muted-foreground">
												{t("common.email", "Email")}
											</div>
											<p>{workforce.candidate.email}</p>
										</div>
									)}
									{workforce.candidate.phone && (
										<div>
											<div className="text-sm text-muted-foreground">
												{t("candidates.list.phone", "Phone")}
											</div>
											<p>{workforce.candidate.phone}</p>
										</div>
									)}
									{workforce.candidate.skills && workforce.candidate.skills.length > 0 && (
										<div>
											<div className="text-sm text-muted-foreground mb-2">
												{t("candidates.list.skills", "Skills")}
											</div>
											<div className="flex flex-wrap gap-1">
												{workforce.candidate.skills.slice(0, 10).map((skill, idx) => (
													<Badge key={idx} variant="secondary" className="text-xs">
														{skill}
													</Badge>
												))}
												{workforce.candidate.skills.length > 10 && (
													<Badge variant="outline" className="text-xs">
														+{workforce.candidate.skills.length - 10}
													</Badge>
												)}
											</div>
										</div>
									)}
								</>
							)}
						</CardContent>
					</Card>
				</div>
			</TabsContent>

			{/* Financial Tab */}
			<TabsContent value="financial" className="mt-4">
				{(() => {
					const hasDailyRates = workforce.purchase_price_daily || workforce.selling_price_daily;
					const hasHourlyRates = workforce.purchase_price_hourly || workforce.selling_price_hourly;
					const hasBillingInfo = workforce.billing_type || workforce.contract_type || workforce.contract_reference;
					const hasAnyFinancialData = hasDailyRates || hasHourlyRates || hasBillingInfo;

					if (!hasAnyFinancialData) {
						return (
							<Card>
								<CardContent className="py-12 text-center text-muted-foreground">
									{t("workforce.noFinancialData", "No financial data available")}
								</CardContent>
							</Card>
						);
					}

					return (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{/* Daily Rates */}
							{hasDailyRates && (
								<Card>
									<CardHeader>
										<CardTitle className="text-lg">
											{t("workforce.fields.dailyRates", "Daily Rates")}
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-4">
										<div className="grid grid-cols-2 gap-4">
											<div>
												<div className="text-sm text-muted-foreground">
													{t("workforce.fields.purchasePrice", "Purchase Price")}
												</div>
												<p className="text-lg font-semibold">
													{workforce.purchase_price_daily
														? `${workforce.currency} ${workforce.purchase_price_daily}`
														: '-'}
												</p>
												<p className="text-xs text-muted-foreground">
													{t("workforce.fields.purchasePriceHint", "What you pay the consultant")}
												</p>
											</div>
											<div>
												<div className="text-sm text-muted-foreground">
													{t("workforce.fields.sellingPrice", "Selling Price")}
												</div>
												<p className="text-lg font-semibold">
													{workforce.selling_price_daily
														? `${workforce.currency} ${workforce.selling_price_daily}`
														: '-'}
												</p>
												<p className="text-xs text-muted-foreground">
													{t("workforce.fields.sellingPriceHint", "What you charge the client")}
												</p>
											</div>
										</div>
										{dailyMargin !== null && (
											<div className="pt-4 border-t">
												<div className="text-sm text-muted-foreground mb-1">
													{t("workforce.fields.dailyMargin", "Daily Margin")}
												</div>
												<Badge variant="secondary" className="text-lg px-3 py-1">
													{dailyMargin.toFixed(1)}%
												</Badge>
												{workforce.purchase_price_daily && workforce.selling_price_daily && (
													<p className="text-sm text-muted-foreground mt-1">
														= {workforce.currency} {(workforce.selling_price_daily - workforce.purchase_price_daily).toFixed(2)}/day
													</p>
												)}
											</div>
										)}
									</CardContent>
								</Card>
							)}

							{/* Hourly Rates */}
							{hasHourlyRates && (
								<Card>
									<CardHeader>
										<CardTitle className="text-lg">
											{t("workforce.fields.hourlyRates", "Hourly Rates")}
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-4">
										<div className="grid grid-cols-2 gap-4">
											<div>
												<div className="text-sm text-muted-foreground">
													{t("workforce.fields.purchasePrice", "Purchase Price")}
												</div>
												<p className="text-lg font-semibold">
													{workforce.purchase_price_hourly
														? `${workforce.currency} ${workforce.purchase_price_hourly}`
														: '-'}
												</p>
												<p className="text-xs text-muted-foreground">
													{t("workforce.fields.purchasePriceHint", "What you pay the consultant")}
												</p>
											</div>
											<div>
												<div className="text-sm text-muted-foreground">
													{t("workforce.fields.sellingPrice", "Selling Price")}
												</div>
												<p className="text-lg font-semibold">
													{workforce.selling_price_hourly
														? `${workforce.currency} ${workforce.selling_price_hourly}`
														: '-'}
												</p>
												<p className="text-xs text-muted-foreground">
													{t("workforce.fields.sellingPriceHint", "What you charge the client")}
												</p>
											</div>
										</div>
										{hourlyMargin !== null && (
											<div className="pt-4 border-t">
												<div className="text-sm text-muted-foreground mb-1">
													{t("workforce.fields.hourlyMargin", "Hourly Margin")}
												</div>
												<Badge variant="secondary" className="text-lg px-3 py-1">
													{hourlyMargin.toFixed(1)}%
												</Badge>
												{workforce.purchase_price_hourly && workforce.selling_price_hourly && (
													<p className="text-sm text-muted-foreground mt-1">
														= {workforce.currency} {(workforce.selling_price_hourly - workforce.purchase_price_hourly).toFixed(2)}/hour
													</p>
												)}
											</div>
										)}
									</CardContent>
								</Card>
							)}

							{/* Billing Info */}
							{hasBillingInfo && (
								<Card className="md:col-span-2">
									<CardHeader>
										<CardTitle className="text-lg">
											{t("workforce.fields.billingType", "Billing Information")}
										</CardTitle>
									</CardHeader>
									<CardContent>
										<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
											{workforce.currency && (
												<div>
													<div className="text-sm text-muted-foreground">
														{t("workforce.fields.currency", "Currency")}
													</div>
													<p className="font-medium">{workforce.currency}</p>
												</div>
											)}
											{workforce.billing_type && (
												<div>
													<div className="text-sm text-muted-foreground">
														{t("workforce.fields.billingType", "Billing Type")}
													</div>
													<p className="font-medium">{workforce.billing_type}</p>
												</div>
											)}
											{workforce.contract_type && (
												<div>
													<div className="text-sm text-muted-foreground">
														{t("workforce.fields.contractType", "Contract Type")}
													</div>
													<p className="font-medium">{workforce.contract_type}</p>
												</div>
											)}
											{workforce.contract_reference && (
												<div>
													<div className="text-sm text-muted-foreground">
														{t("workforce.fields.contractReference", "Contract Reference")}
													</div>
													<p className="font-medium">{workforce.contract_reference}</p>
												</div>
											)}
										</div>
									</CardContent>
								</Card>
							)}
						</div>
					);
				})()}
			</TabsContent>

			{/* Comments Tab */}
			<TabsContent value="comments" className="mt-4">
				<WorkforceComments
					comments={comments}
					onAddComment={onAddComment}
					onDeleteComment={onDeleteComment}
					onUpdateComment={onUpdateComment}
				/>
			</TabsContent>

			{/* Documents Tab */}
			<TabsContent value="documents" className="mt-4">
				<WorkforceDocumentsTab workforceId={workforce.id} />
			</TabsContent>

			{/* History Tab */}
			<TabsContent value="history" className="mt-4">
				<WorkforceHistoryTab workforceId={workforce.id} />
			</TabsContent>
		</Tabs>
	);
};
